import type { ReactNode, SVGProps } from "react";

/**
 * Proof's icons (docs/design/direction.md §6): drawn for the product on a 20 px grid with a
 * 1.5 px stroke, round caps and joins, no fills, in `currentColor`. Each draws Minket's own
 * object (a micro seal for a domain, a thread with a knot for a webhook, a signature over a rule
 * for the audit log) and stands beside a word, never alone and never in a tinted tile.
 *
 * One module per icon (`@minket/brand/icons/domains`), so a page pays only for the icons it
 * draws. Decorative by default (`aria-hidden`): the word beside it names it. Give a `title`
 * only where no word does, and it becomes an image with that name.
 */
export interface IconProps extends Omit<SVGProps<SVGSVGElement>, "children"> {
  /** Width and height in px (20, the grid it is drawn on); a CSS size overrides it. */
  size?: number | string;
  /** The accessible name, for an icon with no word beside it (set as `aria-label`). */
  title?: string;
}

/** The type of every icon component, for tables of pages and actions. */
export type IconComponent = (props: IconProps) => ReactNode;

export function Icon({
  size = 20,
  title,
  children,
  ...props
}: IconProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={title ? undefined : true}
      role={title ? "img" : undefined}
      aria-label={title}
      {...props}
    >
      {children}
    </svg>
  );
}
